const META_PREFIX = "# LMS_META=";
const TESTS_PREFIX = "# LMS_TESTS=";

export function emptyNotebook() {
  return {
    allowAddCells: true,
    maxCells: 20,
    cells: [
      {
        id: "md-1",
        type: "markdown",
        readOnly: false,
        source: "# Lesson\n\nWrite instructions here.",
      },
      {
        id: "code-1",
        type: "code",
        readOnly: false,
        source: "import marimo as mo\n\nmo.md(\"Ready to code\")",
      },
    ],
  };
}

function escapeTripleQuotes(text) {
  return String(text || "").replace(/"""/g, '\\"\\"\\"');
}

function cleanTests(tests) {
  if (!Array.isArray(tests)) return [];
  return tests.slice(0, 20).map((test) => {
    const rawValues = Array.isArray(test?.Value) ? test.Value : [];
    const values = rawValues.map((value) => String(value ?? "").slice(0, 500)).slice(0, 20);
    const keywords = Array.isArray(test?.keywords)
      ? test.keywords
      : Array.isArray(test?.Keywords)
        ? test.Keywords
        : [];
    return {
      Value: values.length ? values : [""],
      Output: String(test?.Output ?? test?.expected ?? "").slice(0, 4000),
      title: String(test?.title ?? test?.name ?? "").slice(0, 120),
      description: String(test?.description ?? "").slice(0, 500),
      errorMessage: String(test?.errorMessage ?? test?.error_message ?? "").slice(0, 1000),
      type: String(test?.type ?? "").slice(0, 40),
      keywords: keywords.map((key) => String(key ?? "").slice(0, 120)).slice(0, 20),
    };
  });
}

function testsForCell(cell, testsByCell) {
  if (!cell || cell.type === "markdown") return [];
  const embedded = Array.isArray(cell.tests) ? cell.tests : [];
  const mapped = testsByCell && Array.isArray(testsByCell[cell.id]) ? testsByCell[cell.id] : [];
  return cleanTests(embedded.length ? embedded : mapped);
}

function testsByCellId(cells) {
  const mapped = {};
  (cells || []).forEach((cell) => {
    if (!cell || cell.type === "markdown") return;
    const tests = cleanTests(cell.tests);
    if (tests.length && cell.id) mapped[cell.id] = tests;
  });
  return mapped;
}

function readJsonLine(source, prefix) {
  const line = String(source || "").split(/\r?\n/).find((item) => item.startsWith(prefix));
  if (!line) return null;
  try {
    return JSON.parse(line.slice(prefix.length).replace(/\r$/, "").trim());
  } catch {
    return null;
  }
}

function indentCode(source) {
  const lines = String(source || "").split("\n");
  if (!lines.some((line) => line.trim())) {
    return "    pass";
  }
  return lines.map((line) => `    ${line}`).join("\n");
}

export function serializeNotebook(doc) {
  const allowAddCells = doc.allowAddCells !== false;
  const maxCells = Number(doc.maxCells) > 0 ? Number(doc.maxCells) : 20;
  const cells = Array.isArray(doc.cells) ? doc.cells : [];
  const testsByCell = testsByCellId(cells);
  const meta = JSON.stringify({
    allowAddCells,
    maxCells,
    testsByCell,
    cells: cells.map((cell) => ({
      id: cell.id,
      type: cell.type === "markdown" ? "markdown" : "code",
      readOnly: !!cell.readOnly,
      source: cell.source || "",
      functionCall: cell.type === "markdown" ? "" : String(cell.functionCall || cell.function_call || "").slice(0, 500),
      tests: cell.type === "markdown" ? [] : cleanTests(cell.tests),
    })),
  });

  const blocks = cells.map((cell) => {
    const readOnly = !!cell.readOnly;
    const decorator = readOnly ? "@app.cell(hide_code=True)" : "@app.cell";
    if (cell.type === "markdown") {
      return `${decorator}
def _():
    import marimo as mo
    mo.md(r"""${escapeTripleQuotes(cell.source)}""")
    return`;
    }
    return `${decorator}
def _():
${indentCode(cell.source)}
    return`;
  }).join("\n\n");

  return `import marimo

${TESTS_PREFIX}${JSON.stringify(testsByCell)}
${META_PREFIX}${meta}

app = marimo.App()

${blocks}

if __name__ == "__main__":
    app.run()
`;
}

const CASE_HEADER_RE = /^(?:#{1,6}\s*)?(?:\*\*|__)?\s*Case\s+\d+\s*:\s*(.*?)(?:\*\*|__)?\s*$/i;
const CASE_SPLIT_RE = /(?=^(?:#{1,6}\s*)?(?:\*\*|__)?\s*Case\s+\d+\s*:)/gim;
const BLANK_VALUE_RE = /^(?:leave\s+blank\.?|blank|none|n\/?a|—|-)$/i;
const FIELD_RES = [
  ["title", /^title\s*:\s*(.*)$/i],
  ["description", /^description\s*:\s*(.*)$/i],
  ["functionCall", /^function\s*call(?:\s*\(optional\))?\s*:\s*(.*)$/i],
  ["keywords", /^assertion\s*keywords(?:\s*\(optional\))?\s*:\s*(.*)$/i],
  ["values", /^values\b[^:\n]*:?\s*(.*)$/i],
  ["output", /^output\s*:\s*(.*)$/i],
  ["errorMessage", /^error\s*message\s*:\s*(.*)$/i],
];

function stripMdLine(line) {
  let text = String(line || "").trim().replace(/^#{1,6}\s*/, "");
  if (text.startsWith("**") && text.endsWith("**") && text.length >= 4) {
    text = text.slice(2, -2).trim();
  }
  return text.replace(/\*\*/g, "").replace(/__/g, "").trim();
}

function meaningful(text) {
  const cleaned = String(text || "").trim();
  if (!cleaned || BLANK_VALUE_RE.test(cleaned)) return "";
  return cleaned;
}

function dedentBlock(text) {
  const lines = String(text || "").replace(/\t/g, "    ").split("\n")
    .filter((line) => !line.trim().startsWith("```"));
  const indents = lines.filter((line) => line.trim()).map((line) => line.length - line.trimStart().length);
  const cut = indents.length ? Math.min(...indents) : 0;
  return lines.map((line) => (line.length >= cut ? line.slice(cut) : line)).join("\n").replace(/^\n+|\n+$/g, "").trim();
}

function valueLines(sameLine, body) {
  const chunks = [];
  if (String(sameLine || "").includes("=")) chunks.push(String(sameLine).trim());
  dedentBlock(body).split("\n").forEach((line) => chunks.push(line));
  return chunks.map((line) => line.trim()).filter((line) => (
    line
    && !line.startsWith("```")
    && !/^(?:add each assignment|enter each|one per line)\b/i.test(line)
  ));
}

function parseCaseBlock(block) {
  const lines = String(block || "").replace(/\r\n/g, "\n").split("\n");
  const header = stripMdLine(lines[0] || "");
  const headerMatch = header.match(CASE_HEADER_RE);
  if (!headerMatch) return null;
  const sections = {};
  FIELD_RES.forEach(([name]) => {
    sections[name] = { same: "", body: [] };
  });
  let current = null;
  lines.slice(1).forEach((raw) => {
    const line = stripMdLine(raw);
    const matched = FIELD_RES.map(([name, pattern]) => {
      const found = line.match(pattern);
      return found ? [name, found[1]] : null;
    }).find(Boolean);
    if (matched) {
      current = matched[0];
      sections[current].same = matched[1];
      return;
    }
    if (current) sections[current].body.push(raw);
  });
  const joined = (name) => [sections[name].same, ...sections[name].body].join(" ").trim();
  const keywordsText = meaningful(joined("keywords"));
  const keywords = keywordsText ? keywordsText.split(",").map((part) => part.trim()).filter(Boolean) : [];
  const functionCall = meaningful(joined("functionCall"));
  return {
    functionCall,
    test: {
      title: meaningful(sections.title.same) || String(headerMatch[1] || "").trim(),
      description: meaningful(joined("description")),
      Value: valueLines(sections.values.same, sections.values.body.join("\n")),
      Output: dedentBlock([sections.output.same, ...sections.output.body].join("\n")),
      errorMessage: meaningful(joined("errorMessage")),
      keywords,
      type: keywords.length ? "assertion" : "",
    },
  };
}

export function splitIpynbTestSpecs(source) {
  const text = String(source || "").replace(/\r\n/g, "\n");
  if (!/(?:^|\n)\s*(?:#{1,6}\s*)?(?:\*\*|__)?\s*Case\s+\d+\s*:/i.test(text)) {
    return { lesson: text, tests: [], functionCall: "" };
  }
  const tests = [];
  let functionCall = "";
  const lesson = [];
  text.split(CASE_SPLIT_RE).forEach((part) => {
    if (/^(?:#{1,6}\s*)?(?:\*\*|__)?\s*Case\s+\d+\s*:/i.test(part.trim())) {
      const parsed = parseCaseBlock(part.replace(/^\n+|\n+$/g, ""));
      if (!parsed) return;
      tests.push(parsed.test);
      if (parsed.functionCall && !functionCall) functionCall = parsed.functionCall;
      return;
    }
    lesson.push(part);
  });
  return { lesson: lesson.join("").trim(), tests, functionCall };
}

function applyIpynbTestSpecs(cells) {
  const specs = cells.map((cell) => {
    if (cell.type !== "markdown") return { tests: [], functionCall: "" };
    const split = splitIpynbTestSpecs(cell.source);
    cell.source = split.lesson;
    return split;
  });
  const targetIndex = (index) => {
    for (let cursor = index + 1; cursor < cells.length; cursor += 1) {
      if (cells[cursor].type === "code") return cursor;
      if (cells[cursor].type === "markdown" && String(cells[cursor].source || "").trim() && !specs[cursor].tests.length) {
        break;
      }
    }
    for (let cursor = index - 1; cursor >= 0; cursor -= 1) {
      if (cells[cursor].type === "code") return cursor;
    }
    for (let cursor = index + 1; cursor < cells.length; cursor += 1) {
      if (cells[cursor].type === "code") return cursor;
    }
    return -1;
  };
  specs.forEach((spec, index) => {
    if (!spec.tests.length) return;
    const target = targetIndex(index);
    if (target < 0) return;
    const cell = cells[target];
    cell.tests = [...(cell.tests || []), ...spec.tests];
    if (spec.functionCall && !cell.functionCall) cell.functionCall = spec.functionCall;
  });
  const kept = cells.filter((cell) => cell.type !== "markdown" || String(cell.source || "").trim());
  return kept.length ? kept : cells;
}

export function notebookFromIpynb(text) {
  let notebook;
  try {
    notebook = JSON.parse(String(text || ""));
  } catch {
    throw new Error("This file is not valid Jupyter JSON.");
  }
  const rawCells = Array.isArray(notebook?.cells) ? notebook.cells : [];
  if (!rawCells.length) {
    throw new Error("The notebook has no cells.");
  }
  const cells = rawCells.map((cell, index) => {
    const source = Array.isArray(cell?.source)
      ? cell.source.join("")
      : String(cell?.source || "");
    const markdown = cell?.cell_type === "markdown" || cell?.cell_type === "raw";
    return {
      id: markdown ? `md-${index + 1}` : `code-${index + 1}`,
      type: markdown ? "markdown" : "code",
      readOnly: false,
      source,
      functionCall: "",
      tests: [],
    };
  });
  const withTests = applyIpynbTestSpecs(cells);
  return {
    allowAddCells: true,
    maxCells: Math.max(20, withTests.length),
    cells: withTests,
  };
}

export function marimoPyFromIpynb(text) {
  return serializeNotebook(notebookFromIpynb(text));
}

export function withNotebookMeta(source, { allowAddCells, maxCells } = {}) {
  const text = String(source || "");
  const meta = JSON.stringify({
    allowAddCells: allowAddCells !== false,
    maxCells: Number(maxCells) > 0 ? Number(maxCells) : 20,
  });
  const line = `${META_PREFIX}${meta}`;
  if (text.includes(META_PREFIX)) {
    return text.replace(new RegExp(`^${META_PREFIX}.*$`, "m"), line);
  }
  if (/import marimo/.test(text)) {
    return text.replace(/import marimo[^\n]*/, (match) => `${match}\n\n${line}`);
  }
  return `${line}\n${text}`;
}

export function parseNotebook(source) {
  const fallback = emptyNotebook();
  if (!source || typeof source !== "string") {
    return fallback;
  }
  const parsed = readJsonLine(source, META_PREFIX);
  const testsByCell = readJsonLine(source, TESTS_PREFIX) || parsed?.testsByCell || {};
  if (parsed && Array.isArray(parsed.cells) && parsed.cells.length) {
    return {
      allowAddCells: parsed.allowAddCells !== false,
      maxCells: Number(parsed.maxCells) > 0 ? Number(parsed.maxCells) : 20,
      cells: parsed.cells.map((cell, index) => ({
        id: cell.id || `cell-${index + 1}`,
        type: cell.type === "markdown" ? "markdown" : "code",
        readOnly: !!cell.readOnly,
        source: cell.source || "",
        functionCall: String(cell.functionCall || cell.function_call || ""),
        tests: testsForCell(cell, testsByCell),
      })),
    };
  }
  return {
    ...fallback,
    cells: [
      {
        id: "imported-1",
        type: "code",
        readOnly: false,
        source,
      },
    ],
  };
}
